import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
  Container,
  Drawer,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@mui/material/styles";
import SettingsContext from "src/context/SettingsContext";
import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import Logo from "./../../component/Logo";
import { BiMoon, BiSun } from "react-icons/bi";
import { AuthContext } from "src/context/Auth";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";

export default function Header() {
  const auth = useContext(AuthContext);
  const themeSeeting = useContext(SettingsContext);

  // Updated theme change function with shared theme management
  const changeTheme = (type) => {
    themeSeeting.saveSettings({
      theme: type,
    });
    // This will be handled by the saveSettings function in SettingsContext
    // which now calls SharedThemeManager.setTheme()
  };

  const isLightTheme = themeSeeting.settings.theme === "LIGHT";
  const [scroll, setScroll] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const isBlogPage = location.pathname.startsWith("/blog");

  // Material UI hook for screen size detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    setMobileOpen(open);
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setScroll(window.scrollY > 250);
    });
  }, []);

  useEffect(() => {
    if (isBlogPage && themeSeeting.settings.theme !== "LIGHT") {
      changeTheme("LIGHT");
    }
  }, [isBlogPage, themeSeeting.settings.theme]);

  const navLinks = [
    {
      label: "Features",
      path: "/features",
      external: false,
    },
    {
      label: "How it works?",
      path: "/how-it-works",
      external: false,
    },
    {
      label: "For Accountants",
      path: "/for-accountants",
      external: false,
    },
    {
      label: "Blog",
      path: "/blog",
      external: true,
      nextPath: "/blog",
    },
    {
      label: "Pricing",
      path: "/pricing",
      external: false,
    },
  ];

  const handleNavigation = (link) => {
    if (link.external && typeof themeSeeting.navigateToNextApp === "function") {
      themeSeeting.navigateToNextApp(link.nextPath);
    } else if (link.external) {
      // navigateToNextApp isn't wired up yet - fall back to a plain in-app navigation
      // rather than silently doing nothing when this link is clicked.
      history.push(link.path);
    }
    // For internal navigation, Link component will handle it
  };

  const blogButton = (
    <Box
      display="flex"
      alignItems="center"
      flexDirection={isMobile ? "column" : "row"}
    >
      {navLinks.map((link) =>
        link.external ? (
          <span
            key={link.label}
            onClick={() => handleNavigation(link)}
            style={{
              textDecoration: "none",
              color: isLightTheme ? "#666" : "#ccc",
              fontSize: "large",
              fontWeight: "normal",
              margin: "0 20px",
              cursor: "pointer",
              position: "relative",
              paddingBottom: "5px",
            }}
          >
            {link.label}
          </span>
        ) : (
          <Link
            key={link.label}
            to={link.path}
            style={{
              textDecoration: "none",
              color:
                location.pathname === link.path
                  ? isLightTheme
                    ? "#000"
                    : "#fff"
                  : isLightTheme
                  ? "#666"
                  : "#ccc",
              fontSize: "large",
              fontWeight: location.pathname === link.path ? "bold" : "normal",
              margin: "0 20px",
              cursor: "pointer",
              position: "relative",
              paddingBottom: "5px",
            }}
          >
            {link.label}
            {location.pathname === link.path && (
              <span
                style={{
                  display: "block",
                  height: "2px",
                  background:
                    "linear-gradient(266.46deg, #5F51DE 0%, #AD5DD3 100%)",
                  width: "100%",
                  position: "absolute",
                  bottom: "0",
                  left: "0",
                }}
              ></span>
            )}
          </Link>
        )
      )}
    </Box>
  );

  const femmecubatorLogo = (
    <Box>
      <Link to="/" style={{ cursor: "pointer" }}>
        <Logo className="logoImg" />
      </Link>
    </Box>
  );

  const topMenu = (
    <Box
      display="flex"
      flexDirection={isMobile ? "column" : "row"}
      alignItems="center"
    >
      {!isBlogPage && themeSeeting.settings.theme === "LIGHT" && (
        <IconButton onClick={() => changeTheme("DARK")}>
          <BiSun style={{ color: "rgb(84 84 84)" }} />
        </IconButton>
      )}
      {!isBlogPage && themeSeeting.settings.theme === "DARK" && (
        <IconButton onClick={() => changeTheme("LIGHT")}>
          <BiMoon style={{ color: "rgb(84 84 84)" }} />
        </IconButton>
      )}

      {!auth.userLoggedIn && (
        <>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            to="/secure/login"
            component={Link}
            style={{ margin: isMobile ? "10px 0" : "0 10px" }}
          >
            Log In
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            to="/secure/get-started"
            component={Link}
          >
            Get Started
          </Button>
        </>
      )}
      {auth.userLoggedIn && (
        <Button
          variant="contained"
          color="primary"
          size="large"
          to="/secure/portfolio"
          component={Link}
          style={{ margin: isMobile ? "10px 0" : "0 10px" }}
        >
          Dashboard
        </Button>
      )}
    </Box>
  );

  // Mobile Drawer Menu
  const mobileMenu = (
    <Box
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
      p={2}
    >
      <Box display="flex" justifyContent="flex-end">
        <IconButton onClick={toggleDrawer(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      {blogButton}
      {topMenu}
    </Box>
  );

  return (
    <AppBar
      position={history.location.pathname !== "/" ? "relative" : "absolute"}
      elevation={0}
      className={
        themeSeeting.settings.theme === "DARK"
          ? `${scroll ? "navbar-scroll_dark" : "navbar"}`
          : `${scroll ? "navbar-scroll" : "navbar"}`
      }
      style={{
        backgroundColor: "transparent",
        borderRadius: "0px",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar className="NavBar dflexBetween">
          <Box display="flex" alignItems="center">
            {femmecubatorLogo}
          </Box>

          {!isMobile && (
            <>
              <Box flexGrow={1} display="flex" justifyContent="center">
                {blogButton}
              </Box>
              <Box display="flex" justifyContent="flex-end">
                {topMenu}
              </Box>
            </>
          )}

          {isMobile && (
            <IconButton onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          style: {
            backgroundColor: isLightTheme ? "#fff" : "#1e1e1e",
            color: isLightTheme ? "#000" : "#fff", // ensure text is readable
          },
        }}
      >
        {mobileMenu}
      </Drawer>
    </AppBar>
  );
}
